import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { KcAiAnswerComponent, KcCitation } from '../ui';
import { KnowledgeAnswer, KnowledgeAnswerCitation } from '../../../core/models/learn/knowledge-answer';

/**
 * Fills the search overlay's reserved AI-answer slot (`kc-ai-answer-slot`) with the #50 grounded
 * answer. Three states, all driven by the server response:
 *
 *  - LOADING — a calm skeleton while the answer is fetched.
 *  - ESCALATED — a money/account/complaint question was routed to a HUMAN server-side; we show the
 *    "talk to a human" path (`kc-ai-escalate`) INSTEAD of an answer. The persistent "educational, not
 *    financial advice" line is still shown.
 *  - GROUNDED — the extractive answer (reusing `kc-ai-answer` for the answer + citations +
 *    provenance/not-advice footer), with inline citations that route into the reader and a 👍/👎
 *    feedback control that POSTs against the answer's audit id.
 *
 * The "educational, not financial advice" framing is ALWAYS present (the footer in the grounded
 * state, an explicit line in the escalated state) — it is a regulated, persistent guardrail.
 *
 * Stable testids (the orchestrator's @smoke asserts these): `kc-ai-answer`, `kc-ai-citation`,
 * `kc-ai-not-advice`, `kc-ai-escalate`, `kc-ai-feedback-up`, `kc-ai-feedback-down`.
 */
@Component({
  selector: 'app-knowledge-ai-answer',
  standalone: true,
  imports: [CommonModule, RouterModule, KcAiAnswerComponent],
  template: `
    <div
      class="tw-mb-4"
      data-testid="kc-ai-answer-slot"
    >
      <!-- LOADING -->
      <kc-ai-answer *ngIf="loading" [loading]="true" [question]="query"></kc-ai-answer>

      <ng-container *ngIf="!loading && answer">
        <!-- ESCALATED → talk to a human (no answer) -->
        <div
          *ngIf="answer.escalated"
          data-testid="kc-ai-escalate"
          class="tw-rounded-[16px] tw-border tw-border-[#f0d9d8] tw-bg-[#fdf6f5] tw-overflow-hidden"
        >
          <div class="tw-px-6 tw-pt-5 tw-pb-4">
            <div class="tw-flex tw-items-center tw-gap-2 tw-mb-[10px] tw-text-[13px] tw-font-semibold tw-text-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="tw-flex-shrink-0">
                <circle cx="12" cy="12" r="10" /><path d="M12 8v5M12 16h.01" />
              </svg>
              <span>Let's get you to the right person</span>
            </div>
            <p class="tw-text-[15px] tw-leading-[1.65] tw-text-[#3a3a3a] tw-m-0">
              {{ answer.escalationMessage }}
            </p>
            <a
              *ngIf="answer.supportPath"
              [routerLink]="answer.supportPath"
              data-testid="kc-ai-talk-to-human"
              class="tw-inline-flex tw-items-center tw-gap-2 tw-mt-4 tw-bg-primary tw-text-white tw-no-underline tw-text-[13.5px] tw-font-semibold tw-rounded-[5px] tw-px-[18px] tw-py-[10px]"
            >
              Talk to a human
            </a>
          </div>
          <!-- persistent not-financial-advice line (escalated state) -->
          <div
            data-testid="kc-ai-not-advice"
            class="tw-flex tw-items-center tw-gap-[7px] tw-px-6 tw-py-[13px] tw-border-t tw-border-[#f1f1f1] tw-text-[11.5px] tw-text-[#9aa0a6]"
          >
            <span>{{ answer.notFinancialAdviceText }}</span>
          </div>
        </div>

        <!-- GROUNDED answer (reuses kc-ai-answer for answer + citations + not-advice footer) -->
        <div *ngIf="!answer.escalated" data-testid="kc-ai-answer">
          <kc-ai-answer
            [question]="query"
            [answer]="answer.answer"
            [citations]="cardCitations"
          ></kc-ai-answer>

          <!-- inline citations as routable links (so the smoke can assert + a reader can open them) -->
          <div *ngIf="answer.citations.length" class="tw-flex tw-flex-wrap tw-gap-2 tw-mt-2">
            <a
              *ngFor="let c of answer.citations"
              data-testid="kc-ai-citation"
              [routerLink]="linkFor(c)"
              class="tw-inline-flex tw-items-center tw-gap-[6px] tw-text-[12.5px] tw-text-[#005DD1] tw-no-underline tw-border tw-border-[#dce7f5] tw-bg-[#f5f9ff] tw-rounded-full tw-px-3 tw-py-[5px]"
            >
              <span>{{ c.title }}</span>
              <span
                *ngIf="c.gated"
                class="tw-text-[10px] tw-font-semibold tw-uppercase tw-text-[#9a6a00]"
                >Members</span
              >
            </a>
          </div>

          <!-- explicit not-financial-advice testid anchor (the kc-ai-answer footer also shows it; this
               is the stable, asserted hook + the regulated line verbatim) -->
          <div
            data-testid="kc-ai-not-advice"
            class="tw-text-[11.5px] tw-text-[#9aa0a6] tw-mt-3"
          >
            {{ answer.notFinancialAdviceText }}
          </div>

          <!-- 👍 / 👎 feedback on the answer -->
          <div class="tw-flex tw-items-center tw-gap-2 tw-mt-3">
            <span class="tw-text-[12.5px] tw-text-[#6b6f76]">Was this helpful?</span>
            <button
              type="button"
              data-testid="kc-ai-feedback-up"
              [disabled]="rated"
              (click)="rate(true)"
              [class.tw-text-primary]="rating === true"
              class="tw-text-[15px] tw-bg-white tw-border tw-border-[#ececec] tw-rounded-[8px] tw-px-3 tw-py-[6px] tw-cursor-pointer disabled:tw-opacity-60"
              aria-label="Helpful"
            >
              👍
            </button>
            <button
              type="button"
              data-testid="kc-ai-feedback-down"
              [disabled]="rated"
              (click)="rate(false)"
              [class.tw-text-primary]="rating === false"
              class="tw-text-[15px] tw-bg-white tw-border tw-border-[#ececec] tw-rounded-[8px] tw-px-3 tw-py-[6px] tw-cursor-pointer disabled:tw-opacity-60"
              aria-label="Not helpful"
            >
              👎
            </button>
            <span *ngIf="rated" class="tw-text-[12px] tw-text-[#9aa0a6]">Thanks for the feedback.</span>
          </div>
        </div>
      </ng-container>
    </div>
  `,
})
export class KnowledgeAiAnswerComponent {
  /** The grounded answer (or escalate payload) for the current query; null until one is fetched. */
  @Input() answer: KnowledgeAnswer | null = null;
  /** The question the answer reflects — shown as the answer heading. */
  @Input() query = '';
  /** True while the answer is being fetched (shows the skeleton). */
  @Input() loading = false;

  /** Emits (helpful, reason?) when the reader rates the answer; the host POSTs it with the audit id. */
  @Output() feedback = new EventEmitter<{ helpful: boolean; reason?: string }>();

  rating: boolean | null = null;
  rated = false;

  /** Maps the API citations to the slim kc-ai-answer citation shape (title + link). */
  get cardCitations(): KcCitation[] {
    return (this.answer?.citations ?? []).map((c) => ({
      title: c.title,
      url: c.gated ? undefined : this.hrefFor(c),
    }));
  }

  linkFor(c: KnowledgeAnswerCitation): (string | undefined)[] {
    return c.type === 'learn' ? ['/learn/read', c.slug] : ['/learn/faq', c.slug];
  }

  private hrefFor(c: KnowledgeAnswerCitation): string {
    return c.type === 'learn' ? `/learn/read/${c.slug}` : `/learn/faq/${c.slug}`;
  }

  rate(helpful: boolean): void {
    if (this.rated) return;
    this.rating = helpful;
    this.rated = true;
    this.feedback.emit({ helpful });
  }
}
